/**
 * binance-api.js
 *
 * Data service for Binance public REST API calls.
 * All requests are routed through the Netlify serverless proxy to avoid CORS.
 *
 * No API key required — all endpoints used here are public.
 *
 * Key functions:
 *   fetchBinanceKlines(symbol, interval, limit)   → OHLCV candle array
 *   fetchBinanceTicker24hr(symbol)                → 24hr stats for one coin
 *   fetchAllBinanceTickers(symbols)               → bulk 24hr stats (one request)
 *   fetchBinanceKlinesBatch(requests)             → parallel klines for many coins
 */

// ─── Imports ──────────────────────────────────────────────────────────────────
import { getBinanceProxyBase, API_MODE } from './api-config';

// ─── Cache ────────────────────────────────────────────────────────────────────
const cache = new Map();

const CACHE_TTL = {
  klines: 5 * 60 * 1000,   // 5 minutes — candles don't change fast
  ticker: 60 * 1000,        // 60 seconds — price/volume data
  exchange: 24 * 60 * 60 * 1000, // 24 hours — exchange info / symbol list
};

function isCacheValid(entry, ttl) {
  return entry && Date.now() - entry.timestamp < ttl;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

function getCache(key) {
  return cache.get(key);
}

// ─── Core request helper ──────────────────────────────────────────────────────
/**
 * Makes a GET request through the Binance proxy.
 *
 * In VPS mode: GET /api/binance/api/v3/klines?symbol=BTCUSDT&interval=4h&limit=200
 * In Netlify mode: GET /.netlify/functions/binance-proxy?path=/api/v3/klines&symbol=...
 *
 * @param {string} path        Binance API path, e.g. "/api/v3/klines"
 * @param {Object} params      Query parameters to forward
 * @returns {Promise<any>}     Parsed JSON response
 */
async function binanceRequest(path, params = {}) {
  const base = getBinanceProxyBase();
  let url;
  if (API_MODE === 'vps' || base.startsWith('https://api.binance.com')) {
    // VPS or direct: path becomes part of the URL, params are query string
    const urlParams = new URLSearchParams(params);
    url = `${base}${path}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  } else {
    // Netlify: path is a query param
    const urlParams = new URLSearchParams({ path, ...params });
    url = `${base}?${urlParams.toString()}`;
  }

  console.log(`[Binance] ${path}`, params);

  const response = await fetch(url);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const msg = errorBody.message || errorBody.msg || response.statusText;
    // 400 "Invalid symbol" just means this coin isn't listed on Binance — expected, not an error
    if (response.status === 400 && msg.toLowerCase().includes('invalid symbol')) {
      throw new Error(`INVALID_SYMBOL:${params.symbol || path}`);
    }
    throw new Error(`Binance API error ${response.status}: ${msg}`);
  }

  return response.json();
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch OHLCV candlestick data for a single symbol.
 *
 * @param {string} symbol    Binance symbol, e.g. "BTCUSDT"
 * @param {string} interval  Candle interval: "4h" | "1d" | "1w" | "1h" | "15m" etc.
 * @param {number} limit     Number of candles (max 1000). Default 200.
 * @returns {Promise<Array>} Raw Binance kline arrays — pass through parseBinanceKlines() in timeframe-utils.js
 */
export async function fetchBinanceKlines(symbol, interval, limit = 200) {
  const cacheKey = `klines_${symbol}_${interval}_${limit}`;
  const cached = getCache(cacheKey);

  if (isCacheValid(cached, CACHE_TTL.klines)) {
    console.log(`[Binance cache] klines ${symbol} ${interval}`);
    return cached.data;
  }

  try {
    const data = await binanceRequest('/api/v3/klines', {
      symbol,
      interval,
      limit: String(limit),
    });

    setCache(cacheKey, data);
    return data;
  } catch (error) {
    if (error.message.startsWith('INVALID_SYMBOL:')) {
      // Coin simply isn't listed on Binance — log quietly and return null
      console.warn(`[Binance] ${symbol} not listed on Binance, skipping`);
    } else {
      console.error(`[Binance] Failed to fetch klines for ${symbol} ${interval}:`, error);
    }
    return cached?.data ?? null;
  }
}

/**
 * Fetch 24-hour ticker statistics for a single symbol.
 *
 * @param {string} symbol  e.g. "BTCUSDT"
 * @returns {Promise<Object>} Binance 24hr ticker object
 */
export async function fetchBinanceTicker24hr(symbol) {
  const cacheKey = `ticker24_${symbol}`;
  const cached = getCache(cacheKey);

  if (isCacheValid(cached, CACHE_TTL.ticker)) {
    return cached.data;
  }

  try {
    const data = await binanceRequest('/api/v3/ticker/24hr', { symbol });
    setCache(cacheKey, data);
    return data;
  } catch (error) {
    if (error.message.startsWith('INVALID_SYMBOL:')) {
      console.warn(`[Binance] ${symbol} not listed on Binance`);
    } else {
      console.error(`[Binance] Failed to fetch 24hr ticker for ${symbol}:`, error);
    }
    return cached?.data ?? null;
  }
}

/**
 * Bulk-fetch 24-hour ticker statistics for multiple symbols in a SINGLE request.
 * Binance supports: /api/v3/ticker/24hr?symbols=["BTCUSDT","ETHUSDT",...]
 *
 * This replaces the slow sequential per-coin CoinGecko chart fetch loop.
 * One call → data for all 50 coins simultaneously.
 *
 * @param {string[]} symbols  e.g. ["BTCUSDT", "ETHUSDT", "SOLUSDT", ...]
 * @returns {Promise<Object>} Map of symbol → ticker object for easy lookup
 *                            e.g. { "BTCUSDT": { lastPrice, priceChangePercent, volume, ... } }
 */
export async function fetchAllBinanceTickers(symbols) {
  if (!symbols || symbols.length === 0) return {};

  const cacheKey = `tickers_bulk_${symbols.slice().sort().join(',')}`;
  const cached = getCache(cacheKey);

  if (isCacheValid(cached, CACHE_TTL.ticker)) {
    console.log('[Binance cache] bulk tickers');
    return cached.data;
  }

  try {
    // Binance expects the symbols param as a JSON array string
    const symbolsParam = JSON.stringify(symbols);
    const rawArray = await binanceRequest('/api/v3/ticker/24hr', {
      symbols: symbolsParam,
    });

    // Convert array → object keyed by symbol for O(1) lookups
    const tickerMap = {};
    if (Array.isArray(rawArray)) {
      rawArray.forEach(ticker => {
        tickerMap[ticker.symbol] = {
          symbol: ticker.symbol,
          lastPrice: parseFloat(ticker.lastPrice),
          priceChange: parseFloat(ticker.priceChange),
          priceChangePercent: parseFloat(ticker.priceChangePercent),
          highPrice: parseFloat(ticker.highPrice),
          lowPrice: parseFloat(ticker.lowPrice),
          volume: parseFloat(ticker.volume),
          quoteVolume: parseFloat(ticker.quoteVolume), // volume in USD (for USDT pairs)
          openPrice: parseFloat(ticker.openPrice),
          count: ticker.count, // number of trades
        };
      });
    }

    setCache(cacheKey, tickerMap);
    return tickerMap;
  } catch (error) {
    // Binance rejects the entire bulk request if even one symbol is invalid.
    // Fall back to fetching each ticker individually so valid coins still get data.
    console.warn('[Binance] Bulk ticker failed, falling back to individual requests:', error.message);
    return fetchTickersIndividually(symbols);
  }
}

/**
 * Fallback: fetch tickers one-by-one using Promise.allSettled.
 * Slower than the bulk endpoint but resilient — one bad symbol can't break the rest.
 * @private
 */
async function fetchTickersIndividually(symbols) {
  const results = await Promise.allSettled(
    symbols.map(symbol =>
      fetchBinanceTicker24hr(symbol).then(data => ({ symbol, data }))
    )
  );

  const tickerMap = {};
  results.forEach(result => {
    if (result.status === 'fulfilled' && result.value?.data) {
      const { symbol, data } = result.value;
      tickerMap[symbol] = data;
    }
  });

  return tickerMap;
}

/**
 * Fetch OHLCV klines for multiple symbols in parallel, processed in chunks.
 *
 * Why chunks? Netlify free tier has ~10-25 concurrent function invocations.
 * Firing 60+ requests at once causes most to queue, time out, and return null
 * — leaving all trend badges stuck on N/A. Processing CHUNK_SIZE at a time
 * keeps concurrency well under the limit while still being fast.
 *
 * @param {Array<{symbol: string, interval: string, limit?: number}>} requests
 * @returns {Promise<Object>} Map of symbol → raw klines array (or null on failure)
 *                            e.g. { "BTCUSDT": [[ts,o,h,l,c,v], ...], "ETHUSDT": [...] }
 */
const BATCH_CHUNK_SIZE = 12;  // ~4–5 chunks for 60 coins; safe for Netlify free tier

export async function fetchBinanceKlinesBatch(requests) {
  if (!requests || requests.length === 0) return {};

  const output = {};

  for (let i = 0; i < requests.length; i += BATCH_CHUNK_SIZE) {
    const chunk = requests.slice(i, i + BATCH_CHUNK_SIZE);

    const results = await Promise.allSettled(
      chunk.map(({ symbol, interval, limit = 200 }) =>
        fetchBinanceKlines(symbol, interval, limit).then(data => ({ symbol, data }))
      )
    );

    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value) {
        output[result.value.symbol] = result.value.data;
      } else if (result.status === 'rejected') {
        console.warn('[Binance batch] One request failed:', result.reason?.message);
      }
    });
  }

  return output;
}

/**
 * Fetch klines for one coin across all three analysis timeframes in parallel.
 * Convenience wrapper used by the Coin Detail page.
 *
 * @param {string} symbol  e.g. "BTCUSDT"
 * @returns {Promise<{fourHour: Array, daily: Array, weekly: Array}>}
 */
export async function fetchKlinesAllTimeframes(symbol) {
  const [fourHour, daily, weekly] = await Promise.allSettled([
    fetchBinanceKlines(symbol, '4h', 200),   // ~33 days of 4H candles
    fetchBinanceKlines(symbol, '1d', 90),    // 90 daily candles
    fetchBinanceKlines(symbol, '1w', 52),    // 52 weekly candles (1 year)
  ]);

  return {
    fourHour: fourHour.status === 'fulfilled' ? fourHour.value : null,
    daily:    daily.status    === 'fulfilled' ? daily.value    : null,
    weekly:   weekly.status   === 'fulfilled' ? weekly.value   : null,
  };
}

// ─── Cache utilities (matches coingecko-api.js interface) ─────────────────────

/**
 * Clear all Binance API cache entries.
 */
export function clearBinanceCache() {
  cache.clear();
  console.log('[Binance] Cache cleared');
}

/**
 * Return a summary of current cache state for the Settings page.
 * @returns {Object} key → { age, valid }
 */
export function getBinanceCacheStatus() {
  const status = {};
  cache.forEach((value, key) => {
    const age = Date.now() - value.timestamp;
    const ttl = key.startsWith('klines') ? CACHE_TTL.klines : CACHE_TTL.ticker;
    status[key] = {
      age: Math.round(age / 1000) + 's',
      valid: age < ttl,
    };
  });
  return status;
}
